/**
 * Formatting helpers.
 *
 * All of them take the active locale explicitly rather than reading it from a global, so a
 * server-rendered or tested component formats deterministically. Dates use Intl — the legacy
 * app called toLocaleDateString() with no locale, so output changed per browser.
 */

import type { Locale } from '@/shared/i18n';

export function formatBytes(bytes: number | null | undefined, locale: Locale = 'vi'): string {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: value < 10 ? 1 : 0 }).format(value)} ${units[exponent]}`;
}

export function formatNumber(value: number | null | undefined, locale: Locale = 'vi'): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(locale).format(value);
}

/** 1.2K / 3.4M — for social counts where exactness does not matter. */
export function formatCompact(value: number | null | undefined, locale: Locale = 'vi'): string {
  if (value === null || value === undefined) return '—';
  return new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

/** mm:ss, or h:mm:ss past an hour. */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '—';
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(seconds)}` : `${minutes}:${pad(seconds)}`;
}

/** mm:ss.mmm — timeline and caption timing need sub-second precision. */
export function formatTimecode(ms: number): string {
  const safe = Math.max(0, Math.round(ms));
  const minutes = Math.floor(safe / 60_000);
  const seconds = Math.floor((safe % 60_000) / 1000);
  const millis = safe % 1000;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

/** Parse "mm:ss.mmm" or "mm:ss" back to milliseconds. Returns null when unparseable. */
export function parseTimecode(value: string): number | null {
  const match = value.trim().match(/^(\d{1,2}):(\d{1,2})(?:[.,](\d{1,3}))?$/);
  if (!match) return null;
  const minutes = Number(match[1]);
  const seconds = Number(match[2]);
  if (seconds > 59) return null;
  const millis = Number((match[3] ?? '0').padEnd(3, '0'));
  return minutes * 60_000 + seconds * 1000 + millis;
}

export function formatDate(iso: string | null | undefined, locale: Locale = 'vi'): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(date);
}

export function formatDateTime(iso: string | null | undefined, locale: Locale = 'vi'): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

export function formatTime(iso: string | null | undefined, locale: Locale = 'vi'): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(date);
}

/** "3 phút trước" — uses Intl.RelativeTimeFormat so both locales read naturally. */
export function formatRelative(iso: string | null | undefined, locale: Locale = 'vi'): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';

  const deltaSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const absolute = Math.abs(deltaSeconds);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });

  if (absolute < 45) return rtf.format(Math.round(deltaSeconds / 1), 'second');
  if (absolute < 3600) return rtf.format(Math.round(deltaSeconds / 60), 'minute');
  if (absolute < 86_400) return rtf.format(Math.round(deltaSeconds / 3600), 'hour');
  if (absolute < 2_592_000) return rtf.format(Math.round(deltaSeconds / 86_400), 'day');
  return formatDate(iso, locale);
}

export interface ExpiryInfo {
  /** null when the resource has no expiry at all. */
  hoursLeft: number | null;
  isExpired: boolean;
  /** Within 48 hours — the Blueprint's "warn the user" threshold. */
  isUrgent: boolean;
  label: string;
}

/** Turn an expiry timestamp into the chip's tone and text. */
export function describeExpiry(
  expiresAt: string | null | undefined,
  locale: Locale = 'vi',
): ExpiryInfo {
  if (!expiresAt) return { hoursLeft: null, isExpired: false, isUrgent: false, label: '' };

  const target = new Date(expiresAt).getTime();
  if (Number.isNaN(target)) return { hoursLeft: null, isExpired: false, isUrgent: false, label: '' };

  const hoursLeft = (target - Date.now()) / 3_600_000;

  if (hoursLeft <= 0) {
    return {
      hoursLeft: 0,
      isExpired: true,
      isUrgent: true,
      label: locale === 'vi' ? 'Đã hết hạn' : 'Expired',
    };
  }

  if (hoursLeft <= 48) {
    const hours = Math.max(1, Math.round(hoursLeft));
    return {
      hoursLeft,
      isExpired: false,
      isUrgent: true,
      label: locale === 'vi' ? `Hết hạn sau ${hours} giờ` : `Expires in ${hours}h`,
    };
  }

  const days = Math.round(hoursLeft / 24);
  return {
    hoursLeft,
    isExpired: false,
    isUrgent: false,
    label: locale === 'vi' ? `Còn ${days} ngày` : `${days} days left`,
  };
}

export function formatPlatform(platform: string | null | undefined): string {
  switch ((platform ?? '').toLowerCase()) {
    case 'tiktok':
      return 'TikTok';
    case 'douyin':
      return 'Douyin';
    case 'bilibili':
      return 'Bilibili';
    default:
      return platform ?? '—';
  }
}
